import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import type { RootState } from '../store/index';
import { type UserRole } from '../constants';

interface RoleGuardProps {
  children: React.ReactNode;
  allowedRoles: UserRole[];
  fallbackPath?: string;
}

const RoleGuard = ({ children, allowedRoles, fallbackPath = '/dashboard' }: RoleGuardProps) => {
  const { user, token, expiresAt } = useSelector((state: RootState) => state.auth);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is authenticated
    if (!token || !user) {
      navigate('/');
      return;
    }

    // Check if token is expired
    if (expiresAt) {
      const currentTime = new Date().getTime();
      const expirationTime = new Date(expiresAt).getTime();
      
      if (currentTime >= expirationTime) {
        navigate('/');
        return;
      }
    }

    // Check if user has required role
    if (!allowedRoles.includes(user.role as UserRole)) {
      // console.warn(`Access denied: User role '${user.role}' is not in allowed roles:`, allowedRoles); // Removed for production
      navigate(fallbackPath);
      return;
    }
  }, [user, token, expiresAt, allowedRoles, fallbackPath, navigate]);

  // If user is authenticated and has required role, render children
  if (token && user && allowedRoles.includes(user.role as UserRole)) {
    if (expiresAt) {
      const currentTime = new Date().getTime();
      const expirationTime = new Date(expiresAt).getTime();
      
      if (currentTime < expirationTime) {
        return <>{children}</>;
      }
    } else {
      return <>{children}</>;
    }
  }

  // Return null while redirecting
  return null;
};

export default RoleGuard;
